import { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'src/contexts/auth'
import { useCreateSale, useUpdateSale } from 'src/hooks/api/sale'
import { useGetAnimalListByLot } from 'src/hooks/api/animal'
import { CreateSaleDTO, SaleDTO, UpdateSaleDTO } from 'src/dto/sale'
import { Sale, SaleParams } from 'src/domain/sale'
import { calculateTotalSaleValue } from '../_utils/saleCalculations'
import { useAnimalSaleOperations } from './useAnimalSaleOperations'
import { useSaleDelete } from './useSaleDelete'

interface UseSaleDetailProps {
  saleData: SaleDTO | undefined | null
  onClose: () => void
}

export const useSaleDetail = ({ onClose, saleData }: UseSaleDetailProps) => {
  const [saveLoading, setSaveLoading] = useState(false)
  const [multiSale, setMultiSale] = useState(false)
  const [totalLotWeight, setTotalLotWeight] = useState<number>(0)

  const { userFarm: farmId } = useAuth()
  const { mutateAsync: createSaleMutation } = useCreateSale()
  const { mutateAsync: updateSaleMutation } = useUpdateSale()
  const { updateAnimalsOnSale } = useAnimalSaleOperations()
  const queryClient = useQueryClient()

  const { setValue, reset, watch, clearErrors, setError } = useFormContext<Partial<SaleParams>>()

  const { isDeleting, showDeleteConfirmation, setShowDeleteConfirmation, handleDeleteSale, confirmDeleteSale } =
    useSaleDelete({ saleData, onClose })

  const lotId = watch('idLote')
  const saleType = watch('tipoVenda')

  const { data: animalListByLotData = [], isLoading: animalListLoading } = useGetAnimalListByLot(lotId!)

  const calcTotalPriceLotWeigt = useCallback(() => {
    const valorArrobaValue = watch('valorArroba')
    const valorArrobaString = valorArrobaValue || '0'

    const totalPrice = calculateTotalSaleValue(animalListByLotData, valorArrobaString)
    setValue('valorTotal', totalPrice)
  }, [animalListByLotData, setValue, watch])

  const handleCleanForm = useCallback(() => {
    reset({
      tipoVenda: 'animal'
    })
    clearErrors()
  }, [reset, clearErrors])

  // useEffect(() => {
  //   if (!saleData) return

  //   // Prepara os dados para o formulário (SaleParams aceita string ou number)
  //   const formData: Partial<SaleParams> = {
  //     ...saleData,
  //     valorArroba: saleData.valorArroba ? formatCurrencyValueDirect(String(saleData.valorArroba)) : undefined,
  //     valorTotal: saleData.valorTotal ? formatCurrencyValueDirect(String(saleData.valorTotal)) : undefined,
  //     tipoVenda: saleData.idLote ? 'lote' : 'animal'
  //   }

  //   reset(formData)
  // }, [saleData])

  useEffect(() => {
    clearErrors('idLote')

    if (animalListByLotData.length > 0) {
      let totalWeight = 0
      for (const animal of animalListByLotData) {
        if (animal.pesoAtual) {
          totalWeight += parseFloat(String(animal.pesoAtual) || '0')
        }
      }
      setTotalLotWeight(totalWeight)

      if (watch('valorArroba')) {
        calcTotalPriceLotWeigt()
      }
    } else if (animalListByLotData.length === 0 && lotId) {
      setTotalLotWeight(0)
      setError('idLote', {
        type: 'manual',
        message: 'Lote sem animais para venda'
      })
    }
  }, [animalListByLotData.length, saleType, lotId, setError])

  useEffect(() => {
    if (saleType === 'animal' && !saleData?.idVenda) {
      setValue('idLote', undefined)
      clearErrors('idLote')
    }
  }, [saleType])

  const handleMultiSaleToggle = useCallback((checked: boolean) => {
    setMultiSale(checked)
  }, [])

  const onSubmit = async (formData: Partial<SaleParams>): Promise<void> => {
    setSaveLoading(true)

    const sale = new Sale({
      ...formData,
      idFazenda: farmId!
    })

    try {
      if (formData.idVenda) {
        await updateSaleMutation(sale as UpdateSaleDTO)
      } else {
        await createSaleMutation(sale as CreateSaleDTO)
      }

      if (formData.animais && formData.animais.length > 0) {
        await updateAnimalsOnSale(formData.animais, formData.data || new Date().toISOString())
      }

      queryClient.invalidateQueries({ queryKey: ['getSaleList', farmId] })
      toastSyncSuccess(`Venda ${formData?.idVenda ? 'atualizada' : 'salva'} com sucesso!`)

      if (multiSale) {
        handleCleanForm()
        setValue('tipoVenda', formData?.tipoVenda)
        return
      }

      onClose()
    } catch (err: any) {
      console.error('Erro ao salvar venda:', err)
      toast.error('Erro ao salvar os dados, tente novamente.')
    } finally {
      setSaveLoading(false)
    }
  }

  useEffect(() => {
    clearErrors()
  }, [saleType])

  return {
    isLoading: animalListLoading,
    isSaving: saveLoading,
    isDeleting,
    multiSale,
    totalLotWeight,
    showDeleteConfirmation,

    onSubmit,
    calcTotalPriceLotWeigt,
    setMultiSale: handleMultiSaleToggle,
    handleDeleteSale,
    confirmDeleteSale,
    setShowDeleteConfirmation
  }
}
